// Brand resolution engine — resolves a BrandConfig (asset paths) from a
// hostname. To add/edit/remove a domain's branding, edit brand.registry.ts
// instead; this file shouldn't need to change for that.
import { BRAND_DEFINITIONS, BrandDefinition, LOCAL_DEFAULT_BRAND_ID } from './brand.registry';

export interface BrandConfig {
  id: string;
  domainSuffix: string;
  name: string;
  titleSuffix: string;
  assetsPath: string;
  colorsCssPath: string;
  logoPath: string;
  nameLogoPath: string;
  stringLogoPath: string;
  loaderAnimationPath: string;
  // Sidebar/menu-bar logos: full wordmark shown when the menu is expanded,
  // icon-only mark shown when it's collapsed.
  menuFullLogoPath: string;
  menuShortLogoPath: string;
  // Logo shown on registration/school-onboarding screens ("Register Your
  // Institute" and similar).
  registrationLogoPath: string;
  // Background image on the login screens.
  loginImagePath: string;
  // Background image on the school-registration screens.
  registrationBgPath: string;
  // One-line subheading under "Welcome to <brand>" on the login screens.
  loginTagline: string;
  // Description used for link-preview (og:description/twitter:description)
  // meta tags — independent from loginTagline.
  metaDescription: string;
  // Default country for every phone/country-code picker in the app. Dial
  // code with no "+" (e.g. "91"), and both ISO2 casings since different
  // picker libraries expect different case.
  defaultCountryDialCode: string;
  defaultCountryIso2Lower: string;
  defaultCountryIso2Upper: string;
  // URL for the "Help" link on the login screens.
  helpUrl: string;
  // Whether to show "Welcome to <brand>" + tagline over the login
  // background image.
  showBrandingText: boolean;
  // Whether the staff login screen offers "Login with QR Code".
  staffQrLoginEnabled: boolean;
  // Whether the staff edit form shows a "Send Password" button.
  enableSendStaffPassword: boolean;
  // Whether the admin dashboard shows the "<brand> App Status" tab.
  appStatusTabEnabled: boolean;
  // Whether the school registration flow verifies a mobile OTP.
  mobileNumberVerificationWhileRegistration: boolean;
  // Login-screen quote (right-hand panel) — empty string means "fall back
  // to the app's existing random LOGIN_QUOTES behavior" (see
  // brand.registry.ts's loginQuote doc comment).
  loginQuote: string;
  authorName: string;
  authorNameDisplay: boolean;
  // Parent portal login methods (see brand.registry.ts).
  parentMobileLogin: boolean;
  parentEmailLogin: boolean;
  // i18n key for the student's national ID number field's label (see
  // brand.registry.ts) — always render via `{{ brand.nationalIdFieldLabelKey | translate }}`,
  // never as literal text.
  nationalIdFieldLabelKey: string;
  // i18n keys for the matching document-upload label and its "Upload ..."
  // button text (see brand.registry.ts).
  nationalIdCopyLabelKey: string;
  nationalIdCopyUploadLabelKey: string;
}

const DEFAULT_LOGIN_TAGLINE = 'Platform for Educational Institutions';
const DEFAULT_META_DESCRIPTION = 'Platform for Educational Institutions';
const DEFAULT_COUNTRY_DIAL_CODE = '91';
const DEFAULT_COUNTRY_ISO2 = 'IN';
const DEFAULT_HELP_URL = 'https://bluewings.in/#contact';
const DEFAULT_NATIONAL_ID_FIELD_LABEL_KEY = 'header.aadharuidNumber';
const DEFAULT_NATIONAL_ID_COPY_LABEL_KEY = 'groupsV2.aadharCopy';
const DEFAULT_NATIONAL_ID_COPY_UPLOAD_LABEL_KEY = 'groupsV2.uploadAadharCopy';

// Every brand's assets live at src/assets/brands/<id>/ using this exact
// layout: images/{logo.<ext>,name.svg,string_logo.<ext>,menu_full_logo.<ext>,
// menu_short_logo.<ext>,registration_logo.<ext>}, colors/color.css,
// animations/loader.json. All paths below are derived from `id` — and each
// file's actual extension must match what's really in that folder, or the
// image silently fails to load.
function buildBrandConfig({
  id, domainSuffix, name, logoExt = 'svg', stringLogoExt = 'png', menuFullLogoExt = 'png', menuShortLogoExt = 'png',
  registrationLogoExt = 'png', loginImageExt = 'png', registrationBgExt = 'png',
  loginTagline = DEFAULT_LOGIN_TAGLINE, metaDescription = DEFAULT_META_DESCRIPTION,
  defaultCountryDialCode = DEFAULT_COUNTRY_DIAL_CODE, defaultCountryIso2 = DEFAULT_COUNTRY_ISO2,
  helpUrl = DEFAULT_HELP_URL, showBrandingText = true, staffQrLoginEnabled = true,
  enableSendStaffPassword = true, appStatusTabEnabled = true, mobileNumberVerificationWhileRegistration = true,
  loginQuote = '', authorName = '', authorNameDisplay = false,
  parentMobileLogin = true, parentEmailLogin = true,
  nationalIdFieldLabelKey = DEFAULT_NATIONAL_ID_FIELD_LABEL_KEY,
  nationalIdCopyLabelKey = DEFAULT_NATIONAL_ID_COPY_LABEL_KEY,
  nationalIdCopyUploadLabelKey = DEFAULT_NATIONAL_ID_COPY_UPLOAD_LABEL_KEY,
}: BrandDefinition): BrandConfig {
  const assetsPath = `assets/brands/${id}`;
  return {
    id,
    domainSuffix,
    name,
    titleSuffix: name,
    assetsPath,
    colorsCssPath: `${assetsPath}/colors/color.css`,
    logoPath: `${assetsPath}/images/logo.${logoExt}`,
    nameLogoPath: `${assetsPath}/images/name.svg`,
    stringLogoPath: `${assetsPath}/images/string_logo.${stringLogoExt}`,
    loaderAnimationPath: `${assetsPath}/animations/loader.json`,
    menuFullLogoPath: `${assetsPath}/images/menu_full_logo.${menuFullLogoExt}`,
    menuShortLogoPath: `${assetsPath}/images/menu_short_logo.${menuShortLogoExt}`,
    registrationLogoPath: `${assetsPath}/images/registration_logo.${registrationLogoExt}`,
    loginImagePath: `${assetsPath}/images/login_image.${loginImageExt}`,
    registrationBgPath: `${assetsPath}/images/registration_bg.${registrationBgExt}`,
    loginTagline,
    metaDescription,
    defaultCountryDialCode,
    defaultCountryIso2Lower: defaultCountryIso2.toLowerCase(),
    defaultCountryIso2Upper: defaultCountryIso2.toUpperCase(),
    helpUrl,
    showBrandingText,
    staffQrLoginEnabled,
    enableSendStaffPassword,
    appStatusTabEnabled,
    mobileNumberVerificationWhileRegistration,
    loginQuote,
    authorName,
    authorNameDisplay,
    parentMobileLogin,
    parentEmailLogin,
    nationalIdFieldLabelKey,
    nationalIdCopyLabelKey,
    nationalIdCopyUploadLabelKey,
  };
}

export const brands: BrandConfig[] = BRAND_DEFINITIONS.map(buildBrandConfig);

// Used only when a hostname matches no registered brand and no local
// override applies. Every path is empty — no brand's assets/name are shown
// by default; a new domain must be registered in brand.registry.ts to be
// themed, it never silently inherits bluewings (or any other brand).
export const BLANK_BRAND: BrandConfig = {
  id: '',
  domainSuffix: '',
  name: '',
  titleSuffix: '',
  assetsPath: '',
  colorsCssPath: '',
  logoPath: '',
  nameLogoPath: '',
  stringLogoPath: '',
  loaderAnimationPath: '',
  menuFullLogoPath: '',
  menuShortLogoPath: '',
  registrationLogoPath: '',
  loginImagePath: '',
  registrationBgPath: '',
  loginTagline: '',
  metaDescription: '',
  defaultCountryDialCode: '',
  defaultCountryIso2Lower: '',
  defaultCountryIso2Upper: '',
  helpUrl: '',
  showBrandingText: false,
  staffQrLoginEnabled: false,
  enableSendStaffPassword: false,
  appStatusTabEnabled: false,
  loginQuote: '',
  authorName: '',
  authorNameDisplay: false,
  // Placeholder (pre-resolution) brand: leave both methods/mobile OTP on so
  // nothing flickers off before the real brand is resolved.
  parentMobileLogin: true,
  parentEmailLogin: true,
  mobileNumberVerificationWhileRegistration: true,
  nationalIdFieldLabelKey: '',
  nationalIdCopyLabelKey: '',
  nationalIdCopyUploadLabelKey: '',
};

// Hostnames that don't map to a real domain (local dev, previews), matched
// as an exact host or any subdomain of it — e.g. "demo.localhost" counts as
// "localhost". On these, resolveBrand() uses LOCAL_DEFAULT_BRAND_ID (see
// brand.registry.ts) unless a manual override (see captureBrandOverride)
// says otherwise — so any brand can still be tried without a code change.
const LOCAL_HOSTNAME_SUFFIXES = ['localhost', '127.0.0.1'];

function isLocalHostname(hostname: string): boolean {
  return LOCAL_HOSTNAME_SUFFIXES.some(
    suffix => hostname === suffix || hostname.endsWith(`.${suffix}`)
  );
}

const OVERRIDE_QUERY_PARAM = 'brand';
const OVERRIDE_STORAGE_KEY = 'brandOverride';

// Called once at startup (see BrandService) so a `?brand=<id>` in the URL
// is remembered for the rest of the session, even after the query param
// is gone from later navigations.
export function captureBrandOverride(hostname: string, search: string): void {
  if (!isLocalHostname(hostname)) {
    return;
  }
  const requested = new URLSearchParams(search).get(OVERRIDE_QUERY_PARAM);
  if (!requested) {
    return;
  }
  if (brands.some(brand => brand.id === requested)) {
    localStorage.setItem(OVERRIDE_STORAGE_KEY, requested);
  }
}

export function resolveBrand(hostname: string): BrandConfig {
  if (isLocalHostname(hostname)) {
    const overrideId = localStorage.getItem(OVERRIDE_STORAGE_KEY) || LOCAL_DEFAULT_BRAND_ID;
    const localBrand = brands.find(brand => brand.id === overrideId);
    if (localBrand) {
      return localBrand;
    }
  }
  const match = brands.find(brand => hostname.endsWith(brand.domainSuffix));
  return match || BLANK_BRAND;
}
